import Pagination from "@/components/Pagination";
import { PageTitle } from "@/components/layouts/admin/PageTitle";
import { BaseModal } from "@/components/BaseModal";
import { renderDateString } from "@/helpers/renderDate";
import useFetchAndPaginate from "@/hooks/useFetchAndPaginate";
import { MediaCategory } from "@/interfaces/MediaCategory";
import { MediaContent } from "@/interfaces/MediaContent";
import { MediaCategoryService } from "@/services/media-category.service";
import { MediaContentService } from "@/services/media-content.service";
import { showErrorDialog } from "@/helpers/showErrorDialog";
import { useEffect, useRef, useState } from "react";
import { FaPencilAlt, FaPlus, FaTags, FaTrash } from "react-icons/fa";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

const riskLabel: Record<string, string> = {
  HIGH_RISK: "กลุ่มเสี่ยงสูง",
  LOW_RISK: "กลุ่มเสี่ยงน้อย",
};

const contentService = new MediaContentService();
const categoryService = new MediaCategoryService();

export default function MediaContentsPage() {
  const { items, handleRemove, totalPages, page, setPage } =
    useFetchAndPaginate<MediaContent>(contentService);

  const [catModalOpen, setCatModalOpen] = useState(false);
  const [categories, setCategories] = useState<MediaCategory[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const newNameRef = useRef<HTMLInputElement>(null);

  const fetchCategories = async () => {
    try {
      const data = await categoryService.listAll();
      setCategories(data);
    } catch (error) {
      showErrorDialog(error);
    }
  };

  useEffect(() => {
    if (catModalOpen) fetchCategories();
  }, [catModalOpen]);

  const handleAddCategory = async () => {
    const name = newNameRef.current?.value?.trim();
    if (!name) return;
    Swal.showLoading();
    try {
      await categoryService.create({ name });
      newNameRef.current.value = "";
      await fetchCategories();
      Swal.close();
    } catch (error) {
      Swal.close();
      showErrorDialog(error);
    }
  };

  const handleSaveEdit = async (id: string) => {
    if (!editingName.trim()) return;
    Swal.showLoading();
    try {
      await categoryService.update(id, { name: editingName });
      setEditingId(null);
      await fetchCategories();
      Swal.close();
    } catch (error) {
      Swal.close();
      showErrorDialog(error);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    const result = await Swal.fire({
      title: "ต้องการลบหมวดหมู่ใช่หรือไม่?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "ยืนยัน",
      cancelButtonText: "ยกเลิก",
    });
    if (!result.isConfirmed) return;
    Swal.showLoading();
    try {
      await categoryService.remove(id);
      await fetchCategories();
      Swal.close();
    } catch (error) {
      Swal.close();
      showErrorDialog(error);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="w-full">
        <div className="flex flex-row justify-between items-center">
          <PageTitle title="จัดการสื่อ" />
          <div className="flex gap-2">
            <button
              type="button"
              className="btn btn-outline btn-secondary"
              onClick={() => setCatModalOpen(true)}>
              <FaTags />
              จัดการหมวดหมู่
            </button>
            <Link to="create" className="btn btn-primary">
              <FaPlus />
              เพิ่มสื่อ
            </Link>
          </div>
        </div>

        <div className="card">
          <div className="flex flex-col gap-6 p-6 py-10 card-content">
            <table className="table main-table">
              <thead>
                <tr>
                  <th>ที่</th>
                  <th>หัวข้อ</th>
                  <th>หมวดหมู่</th>
                  <th>ความเสี่ยง</th>
                  <th>วันที่</th>
                  <th>จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-gray-400">
                      ยังไม่มีรายการ
                    </td>
                  </tr>
                ) : (
                  items.map((item, i) => (
                    <tr key={item.id}>
                      <td>{i + 1}</td>
                      <td>{item.title}</td>
                      <td>{item.category?.name ?? "-"}</td>
                      <td>{riskLabel[item.riskLevel] ?? item.riskLevel}</td>
                      <td>
                        {renderDateString(item.updatedAt, "dd MMMM yyyy HH:mm น.")}
                      </td>
                      <td>
                        <div className="flex flex-row items-center gap-2">
                          <Link
                            to={`${item.id}/edit`}
                            className="btn btn-sm rounded btn-primary">
                            <FaPencilAlt /> แก้ไข
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <Pagination totalPages={totalPages} page={page} onChange={setPage} />
          </div>
        </div>
      </div>

      {/* Category Management Modal */}
      <BaseModal
        visible={catModalOpen}
        onClose={() => setCatModalOpen(false)}
        title="จัดการหมวดหมู่"
        width={480}>
        <div className="flex flex-col gap-4">
          <div className="flex gap-2">
            <input
              ref={newNameRef}
              type="text"
              placeholder="ชื่อหมวดหมู่ใหม่"
              className="input input-bordered flex-1"
              onKeyDown={(e) => e.key === "Enter" && handleAddCategory()}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAddCategory}>
              <FaPlus />
            </button>
          </div>

          <div className="flex flex-col gap-2 max-h-[320px] overflow-y-auto">
            {categories.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">ยังไม่มีหมวดหมู่</p>
            )}
            {categories.map((cat) => (
              <div
                key={cat.id}
                className="flex items-center gap-2 border rounded px-3 py-2">
                {editingId === cat.id ? (
                  <>
                    <input
                      autoFocus
                      className="input input-bordered input-sm flex-1"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSaveEdit(cat.id)}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-success"
                      onClick={() => handleSaveEdit(cat.id)}>
                      บันทึก
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => setEditingId(null)}>
                      ยกเลิก
                    </button>
                  </>
                ) : (
                  <>
                    <span className="flex-1">{cat.name}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost"
                      onClick={() => {
                        setEditingId(cat.id);
                        setEditingName(cat.name);
                      }}>
                      <FaPencilAlt />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost text-error"
                      onClick={() => handleDeleteCategory(cat.id)}>
                      <FaTrash />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </BaseModal>
    </div>
  );
}
